/**
 * ThemeScreen — AI-powered app re-skinning.
 *
 * Flow:
 *   1. User types a vibe prompt and hits "Generate theme".
 *   2. We call api.generateTheme → apply it live via applyTheme.
 *   3. "Use this theme" persists it to localStorage (storeTheme).
 *   4. "Set for couple" calls api.setCoupleTheme so both partners get it.
 *   5. "Reset to default" clears localStorage, applies DEFAULT_THEME,
 *      and optionally clears the couple theme.
 *
 * data-testid map:
 *   theme-prompt, theme-generate, theme-name, theme-apply,
 *   theme-set-couple, theme-reset
 */
import { DEFAULT_THEME, type Theme } from "@hyggebam/shared";
import { useState } from "react";
import { api, ApiError } from "../api.ts";
import { applyTheme, clearStoredTheme, getStoredTheme, storeTheme } from "../theme.ts";

// ─── image slot (loading skeleton or actual image) ───────────────────────────
function ImageSlot({
  src,
  testId,
  generating,
  height,
}: {
  src: string | null | undefined;
  testId: string;
  generating: boolean;
  height: number;
}) {
  if (generating) {
    return (
      <div
        data-testid="theme-image-loading"
        style={{
          height,
          borderRadius: 12,
          background: "linear-gradient(90deg, var(--card) 25%, color-mix(in srgb, var(--accent) 12%, var(--card)) 50%, var(--card) 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.4s ease-in-out infinite",
        }}
        aria-label="Loading image…"
      />
    );
  }
  if (!src) return null;
  return (
    <img
      data-testid={testId}
      src={src}
      alt=""
      loading="lazy"
      style={{
        width: "100%",
        height,
        objectFit: "cover",
        borderRadius: 12,
        display: "block",
      }}
    />
  );
}

// ─── palette swatch ──────────────────────────────────────────────────────────
function PaletteSwatch({ palette }: { palette: Theme["palette"] }) {
  const swatches: [string, string][] = [
    ["bg", palette.bg],
    ["accent", palette.accent],
    ["accent2", palette.accent2],
    ["like", palette.like],
    ["never", palette.never],
    ["later", palette.later],
  ];
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        marginTop: 10,
      }}
    >
      {swatches.map(([label, color]) => (
        <div
          key={label}
          title={`${label}: ${color}`}
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: color,
            border: "1.5px solid rgba(255,255,255,0.18)",
          }}
        />
      ))}
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────
export function ThemeScreen({
  token,
  seat,
  coupleThemeId,
  onCoupleChange,
  onApplied,
}: {
  token: string;
  seat: string;
  coupleThemeId: string | null;
  onCoupleChange: (themeId: string | null) => void;
  /** Called after the user saves a theme to this device ("Use this theme"). */
  onApplied?: () => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<Theme | null>(null);
  const [error, setError] = useState("");
  const [applyMsg, setApplyMsg] = useState("");
  const [coupleMsg, setCoupleMsg] = useState("");

  const hasLocalTheme = Boolean(getStoredTheme());
  const isPreviewApplied = preview && getStoredTheme()?.id === preview.id;

  async function generate() {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError("");
    setPreview(null);
    setApplyMsg("");
    setCoupleMsg("");
    try {
      const theme = await api.generateTheme(token, seat, prompt.trim());
      setPreview(theme);
      // Apply live so the user can see it immediately
      applyTheme(theme);
    } catch (e) {
      const msg = e instanceof ApiError
        ? e.status === 429
          ? "Too many requests — wait a moment and try again."
          : e.message
        : "Couldn't generate a theme right now.";
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }

  function applyAndStore() {
    if (!preview) return;
    storeTheme(preview);
    applyTheme(preview);
    setApplyMsg("Theme saved to this device!");
    setTimeout(() => setApplyMsg(""), 3000);
    onApplied?.();
  }

  async function setForCouple() {
    if (!preview) return;
    setCoupleMsg("Setting…");
    try {
      const updated = await api.setCoupleTheme(token, preview.id);
      onCoupleChange(updated.theme_id);
      setCoupleMsg("Set for both partners!");
      setTimeout(() => setCoupleMsg(""), 3000);
    } catch {
      setCoupleMsg("Couldn't update couple theme.");
      setTimeout(() => setCoupleMsg(""), 3000);
    }
  }

  async function reset() {
    clearStoredTheme();
    applyTheme(DEFAULT_THEME);
    setPreview(null);
    setApplyMsg("");
    setCoupleMsg("");
    // Also clear couple theme if one was set
    if (coupleThemeId) {
      try {
        const updated = await api.setCoupleTheme(token, null);
        onCoupleChange(updated.theme_id);
      } catch {
        // non-fatal — local theme is already cleared
      }
    }
  }

  return (
    <div className="scroll" data-testid="settings-theme-section">
      <h1>Theme</h1>
      <p className="sub" style={{ marginBottom: 18 }}>
        Type a vibe and we'll re-skin the whole app in seconds.
      </p>

      {/* ── prompt + generate ── */}
      <label className="field">
        <span>What's your vibe?</span>
        <input
          data-testid="theme-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Snoopy, cyberpunk neon, cozy autumn cabin…"
          onKeyDown={(e) => e.key === "Enter" && !generating && generate()}
        />
      </label>

      <button
        data-testid="theme-generate"
        className="btn"
        disabled={generating || !prompt.trim()}
        onClick={generate}
      >
        {generating ? "Designing your theme…" : "Generate theme"}
      </button>

      {error && (
        <p className="error" style={{ marginTop: 10 }}>
          {error}
        </p>
      )}

      {/* ── image preview area (shown while generating or after) ── */}
      {(generating || preview) && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Background image — hero */}
          <ImageSlot
            src={preview?.images.background}
            testId="theme-image-bg"
            generating={generating}
            height={160}
          />
          {/* Accent image — smaller */}
          <ImageSlot
            src={preview?.images.accent}
            testId="theme-image-accent"
            generating={generating}
            height={80}
          />
        </div>
      )}

      {/* ── preview card ── */}
      {preview && (
        <div
          style={{
            marginTop: 16,
            background: "var(--card)",
            borderRadius: 16,
            padding: "16px 18px",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div
            data-testid="theme-name"
            style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.3px" }}
          >
            {preview.name}
          </div>
          <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>
            "{preview.prompt}"
          </div>
          <PaletteSwatch palette={preview.palette} />

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button
              data-testid="theme-apply"
              className="btn"
              style={{ flex: 1, minWidth: 140 }}
              onClick={applyAndStore}
            >
              {isPreviewApplied ? "Saved to this device" : "Use this theme"}
            </button>
            <button
              data-testid="theme-set-couple"
              className="btn secondary"
              style={{ flex: 1, minWidth: 140 }}
              onClick={setForCouple}
            >
              Set for couple
            </button>
          </div>

          {applyMsg && (
            <p style={{ color: "var(--like)", fontSize: 13, marginTop: 8, textAlign: "center" }}>
              {applyMsg}
            </p>
          )}
          {coupleMsg && (
            <p
              style={{
                color: coupleMsg.startsWith("Couldn't") ? "var(--never)" : "var(--like)",
                fontSize: 13,
                marginTop: 8,
                textAlign: "center",
              }}
            >
              {coupleMsg}
            </p>
          )}
        </div>
      )}

      {/* ── state line ── */}
      <div style={{ marginTop: 20, color: "var(--muted)", fontSize: 13 }}>
        {hasLocalTheme
          ? "A custom theme is active on this device."
          : coupleThemeId
          ? "Using the couple's shared theme."
          : "Using the default Hygge theme."}
      </div>

      {/* ── reset ── */}
      <div style={{ marginTop: 16 }}>
        <button
          data-testid="theme-reset"
          className="btn ghost"
          onClick={reset}
        >
          Reset to default
        </button>
      </div>

      <div style={{ height: 24 }} />
    </div>
  );
}
